import { Check, ChevronDown, X } from 'lucide-react';
import { useTranslations } from 'next-intl';

type DropdownProps = {
  title: string;
  items: { name: string; value: string }[];
  isOpen: boolean;
  onToggle: () => void;
  selectedItems: string[];
  onSelectionChange: (selectedItems: string[]) => void;
  loading: boolean;
};

export function FilterDropdown({
  title,
  items,
  isOpen,
  onToggle,
  selectedItems,
  onSelectionChange,
  loading,
}: DropdownProps) {
  const t = useTranslations('FilterDropdown');

  const toggleItem = (value: string) => {
    const updatedItems = selectedItems.includes(value)
      ? selectedItems.filter(item => item !== value)
      : [...selectedItems, value];
    onSelectionChange(updatedItems);
  };

  const hasSelectedItems = selectedItems.length > 0;

  return (
    <div className="relative inline-block">
      {/* Dropdown Button */}
      <button
        type="button"
        className={`flex w-fit items-center justify-between rounded-[4px] py-[4px] pl-[12px] pr-[10px] ${
          hasSelectedItems || isOpen ? 'border-transparent bg-[#E5EDFF]' : 'border-[#D4D6EC] bg-white'
        } cursor-pointer border`}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="flex items-center">
          <span className="font-bold">{title}</span>
          {selectedItems.length > 0 && (
            <div className="ml-[4px] flex size-[20px] items-center justify-center rounded-full bg-[#B4C9FF] text-xs text-[#1F202C]">
              {selectedItems.length}
            </div>
          )}
        </div>
        {isOpen
          ? <X className="ml-[2px] size-5" />
          : <ChevronDown className="ml-[2px] size-5" />}
      </button>

      {/* Dropdown Content */}
      {isOpen && (
        <div
          className="absolute z-10 mt-[4px] max-h-[420px] w-fit min-w-[200px] overflow-y-auto rounded-[4px] border border-[#D4D6EC] bg-white"
          role="listbox"
        >
          {loading
            ? (
                <div className="px-[12px] py-[8px] text-center text-[#606384]">
                  {t('loading')}
                </div>
              )
            : (
                items.map(item => (
                  <div
                    key={item.value}
                    className="flex cursor-pointer items-center px-[12px] py-[8px]"
                    onClick={() => toggleItem(item.value)}
                    onKeyDown={e => e.key === 'Enter' && toggleItem(item.value)}
                    role="option"
                    tabIndex={0}
                    aria-selected={selectedItems.includes(item.value)}
                  >
                    <div
                      className={`flex size-[20px] shrink-0 items-center justify-center rounded-sm ${
                        selectedItems.includes(item.value)
                          ? 'bg-black text-white'
                          : 'border border-[#D4D6EC] bg-white text-transparent'
                      }`}
                    >
                      {selectedItems.includes(item.value) && <Check className="size-4" />}
                    </div>
                    <span className="ml-[12px] break-words font-normal">
                      {item.name}
                    </span>
                  </div>
                ))
              )}
        </div>
      )}
    </div>
  );
}
